import styled, { css } from 'styled-components';
import {
  ICON_BUTTON_SIZE_TOKENS,
  ICON_BUTTON_VARIANT_EMPHASIS,
  ICON_BUTTON_VARIANT_FILLED,
  ICON_BUTTON_VARIANT_GHOST,
  ICON_BUTTON_VARIANT_TONAL,
  type IconButtonSize,
  type IconButtonVariant,
} from './IconButton.constants';

interface RootProps {
  $size: IconButtonSize;
  $variant: IconButtonVariant;
  $isOpen?: boolean;
}

export const Root = styled.button<RootProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: background ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'},
    color ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};

  ${({ $size }) => {
    const { box, iconSize } = ICON_BUTTON_SIZE_TOKENS[$size];
    return css`
      width: ${box};
      height: ${box};
      & > svg,
      & > .material-symbols-rounded {
        font-size: ${iconSize};
        width: ${iconSize};
        height: ${iconSize};
      }
    `;
  }}

  ${({ $variant, theme: { colors = {} } }) => {
    if ($variant === ICON_BUTTON_VARIANT_TONAL) {
      return css`
        background: ${colors.surfaceContainerHigh ?? '#292932'};
        color: ${colors.onSurface ?? '#E4E1ED'};
        &:hover:not(:disabled) {
          background: ${colors.surfaceContainerHighest ?? '#34343D'};
        }
      `;
    }
    if ($variant === ICON_BUTTON_VARIANT_GHOST) {
      return css`
        color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
        &:hover:not(:disabled) {
          background: ${colors.surfaceContainerHigh ?? '#292932'};
          color: ${colors.onSurface ?? '#E4E1ED'};
        }
      `;
    }
    if ($variant === ICON_BUTTON_VARIANT_EMPHASIS) {
      return css`
        background: ${colors.primaryEmphasis ?? colors.primaryFixed ?? '#CFC9F5'};
        color: ${colors.onPrimaryEmphasis ?? colors.onPrimaryFixed ?? '#1E1A8A'};
        &:hover:not(:disabled) {
          background: ${colors.primaryEmphasisHover ??
          colors.primaryFixedDim ??
          '#BFB8F0'};
        }
      `;
    }
    if ($variant === ICON_BUTTON_VARIANT_FILLED) {
      const primary = colors.primary ?? '#C1C1FF';
      const onPrimary = colors.onPrimary ?? '#1A1A2A';
      return css`
        background: ${primary};
        color: ${onPrimary};
        &:hover:not(:disabled) {
          background: color-mix(in oklab, ${primary} 92%, white);
        }
      `;
    }
    return null;
  }}

  ${({ $isOpen, theme: { colors = {} } }) =>
    $isOpen &&
    css`
      background: ${colors.surfaceContainerHigh ?? '#292932'};
      color: ${colors.onSurface ?? '#E4E1ED'};
    `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }
`;
